import { HandBack, ecLean, upper, type JanusCtx, type Row } from '../janus-engine/ability/context';
import { statusItemId } from '../janus-engine/statuses/expiry';
import { statusScalar } from '../janus-engine/actions/activity';
import { planRecursiveDelete, recursiveDeleteSurvivors } from '../janus-engine/inventory/delete-plan';
import type { GmBoundaryVerdict } from '../janus-engine/statuses/gm-boundary';
import { withAsyncDeadline } from '../shared/async-deadline';

export function recursiveDeleteOperation(url: string, data: Row, href: string): string | null {
    const route = new URL(url, href).pathname.split('/').pop();
    if (route === 'soft_delete_inc.php' && (data.item_type === 'encounter' || data.item_type === 'location')) return data.item_type;
    if (route === 'delete_empty_location_inc.php') return 'location';
    if (route === 'soft_delete_all_locations_except_campaign_campsite_and_campaign_stronghold.php') return 'locations';
    return ({ delete_all_campaign_encounters: 'campaign', delete_all_adventure_encounters: 'adventure',
        derive_encounters_from_location_master_parent_id: 'derive' } as Record<string, string>)[String(route).replace(/\.php$/, '')] ?? null;
}

/** Discovery and every survivor plan finish before queryData may evacuate PCs
 * or POST the author's deletion. Cold rows are SELECTed once; held rows win. */
export async function prepareRecursiveDeletion(url: string, data: Row, deps: {
    encounter(id: string): Promise<Row>;
    resolve(id: string, encounter: Row, run: (ctx: JanusCtx) => GmBoundaryVerdict, sources: string[]): Promise<GmBoundaryVerdict>;
    report(error: unknown): void;
}): Promise<boolean> {
    const operation = recursiveDeleteOperation(url, data, window.location.href);
    if (!operation) return true;
    try {
        const plans = await withAsyncDeadline(async () => {
            const response = await fetch('/options/api/recursive_delete_facts_inc.php', { method: 'POST',
                headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...data, operation }) });
            if (!response.ok) throw new HandBack(`recursive-delete:discovery-unavailable:${response.status}`);
            const facts = await response.json() as { deleted_ids: string[]; creature_ids: string[]; statuses: Row[];
                holder_encounters: Record<string, string>; preserved_creatures?: Row[]; catalog: Row[] };
            if (['location', 'adventure', 'derive'].includes(operation)
                && facts.preserved_creatures?.some(row => ecLean(row, 'item_type') === 'player_character'))
                throw new HandBack('recursive-delete:player-evacuation-not-ported');
            const target = (row: Row): string => upper(statusScalar(row, 'status_target_id'));
            const due = recursiveDeleteSurvivors(facts.deleted_ids, facts.creature_ids, facts.statuses, facts.catalog);
            const ids = new Set(due.map(row => facts.holder_encounters[target(row)] ?? ''));
            if (ids.has('')) throw new HandBack('recursive-delete:survivor-encounter-not-held');
            const result = [];
            for (const eid of ids) {
                const encounter = await deps.encounter(eid);
                const group = due.filter(row => facts.holder_encounters[target(row)] === eid);
                const connection = (row: Row): string => upper(statusScalar(row, 'status_connected_id'));
                const sources = facts.statuses.filter(row => group.some(selected =>
                    target(selected) === target(row) && (upper(statusScalar(selected, 'status_id')) === upper(statusScalar(row, 'status_id'))
                        || connection(selected) !== '' && connection(selected) === connection(row))));
                const verdict = await deps.resolve(eid, encounter,
                    ctx => planRecursiveDelete(ctx, facts.deleted_ids, facts.creature_ids, facts.statuses, facts.catalog, group.map(target)),
                    [...new Set(sources.map(statusItemId))]);
                result.push({ encounter_id: eid, verdict });
            }
            return { plans: result, before: { deleted_ids: facts.deleted_ids,
                statuses: facts.statuses.map(row => Object.fromEntries(Object.entries(row).map(([key, value]) => [key, String(value ?? '')]))) } };
        }, 20000);
        data.recursive_tail_plans = plans.plans;
        data.recursive_delete_before = plans.before;
        return true;
    } catch (error) {
        deps.report(error instanceof HandBack ? error : new HandBack(`recursive-delete:planning-unavailable:${String(error)}`));
        return false;
    }
}
